// Client notification policy — when to fire an in-app sound / OS banner for an
// incoming message. Hand-ported from cyborg7-core's src/lib/notify/policy.ts
// (Slack-parity). Pure + deterministic so it's unit-tested (notify-policy.test.ts).
//
// Rules (per kind):
//  channel_message: skip own → skip if viewing that channel AND tab focused →
//    skip a THREAD REPLY unless this user is in the thread or personally
//    highlighted by it (threadReplyReachesUser) →
//    pref (default mentions_only, matching the server): muted skips;
//    mentions_only requires a mention; all notifies. The default is mentions_only
//    (NOT all) so an un-set channel — including one in a non-active workspace
//    whose pref hasn't loaded — never banners a plain non-mention message.
//  dm_human: skip own → skip if viewing that peer AND focused → notify (no per-person mute).
//  dm_agent: NO own-skip → skip if viewing that agent AND focused → notify.
//  task_*: always notify.

import { isMe, type ViewerIdentity } from "./session-scope.js";

export type NotificationPref = "all" | "mentions_only" | "muted";

export type NotificationKind =
  | {
      kind: "channel_message";
      channelId: string;
      senderId?: string;
      text: string;
      // ─── Thread context (Slack parity) ───
      // Root id when this message is a THREAD REPLY; absent/null for a top-level
      // post. A reply is not channel traffic: it reaches thread participants
      // only, so it must not notify a channel member who isn't in the thread —
      // not even one on "All new messages". See threadReplyReachesUser.
      parentId?: string | null;
      // POSITIVE evidence, supplied by the caller, that this user is in the
      // thread (follows it, or is in the reply's server-parsed mentions[]).
      // Only ever set to true when known — an absent flag means "unproven",
      // never "not a participant", so the policy under-notifies rather than
      // guessing a follower set the client cannot see.
      threadParticipant?: boolean;
    }
  | { kind: "dm_human"; fromUserId: string; text: string }
  | { kind: "dm_agent"; remoteAgentId: string; text: string }
  | { kind: "agent_permission"; remoteAgentId: string; channelId?: string | null }
  | { kind: "task_assigned" }
  | { kind: "task_review_requested" };

export interface NotifyContext {
  /**
   * THE VIEWER, for the own-message skips (decideChannel / decideDmHuman). Pass the
   * user OBJECT (`authState.user`), not an id: a human holds TWO ids (session-scope.ts
   * `ViewerIdentity`) and only the object carries the second one. A bare id still
   * type-checks and degrades to the old single-id compare.
   */
  currentUserId?: ViewerIdentity;
  currentUserName?: string;
  activeChannelId?: string | null;
  viewingDmHumanUserId?: string | null;
  viewingDmAgentId?: string | null;
  channelNotifPrefs: Record<string, NotificationPref>;
  // Do Not Disturb (client-side). When true, suppresses every banner/sound
  // EXCEPT direct personal @mentions (group broadcasts like @channel/@here are
  // also suppressed). Optional + defaults off so prior behavior is unchanged.
  dndActive?: boolean;
  // Injectable for tests; defaults to the real document focus check.
  isTabFocused?: () => boolean;
}

export interface PolicyDecision {
  notify: boolean;
  reason: string;
}

const SKIP = {
  own: "skip:own",
  viewing: "skip:viewing",
  muted: "skip:muted",
  noMention: "skip:no-mention",
  dnd: "skip:dnd",
  notInThread: "skip:not-in-thread",
} as const;

export function defaultIsTabFocused(): boolean {
  if (typeof document === "undefined") return true;
  const visible = document.visibilityState === "visible";
  // hasFocus() distinguishes "tab visible but OS focus elsewhere / screen
  // locked" — visibilityState alone stays 'visible' there and over-suppresses.
  const focused = typeof document.hasFocus === "function" ? document.hasFocus() : true;
  return visible && focused;
}

// Strict mention matcher: group tokens, bracket form, or `@name` with a trailing
// word boundary (so `@Alex` does NOT match a user named `Ale`).
export function hasMention(text: string, currentUserName: string | undefined): boolean {
  if (text.includes("@everyone") || text.includes("@here") || text.includes("@channel"))
    return true;
  if (!currentUserName) return false;
  const lowerText = text.toLowerCase();
  const lowerName = currentUserName.toLowerCase();
  if (lowerText.includes(`@[${lowerName}]`)) return true;
  const needle = `@${lowerName}`;
  let from = 0;
  for (;;) {
    const idx = lowerText.indexOf(needle, from);
    if (idx === -1) return false;
    const next = lowerText.charAt(idx + needle.length);
    if (next === "" || !/[a-z0-9_]/.test(next)) return true;
    from = idx + needle.length;
  }
}

// True when the text contains a channel-wide broadcast token (@everyone/@here/
// @channel/@all).
export function hasBroadcastMention(text: string): boolean {
  return (
    text.includes("@everyone") ||
    text.includes("@here") ||
    text.includes("@channel") ||
    text.includes("@all")
  );
}

// True when the text contains a PERSONAL @mention of the current user (the
// @name / @[name] forms — broadcasts excluded). Shares the word-boundary scan
// with hasMention so `@Alex` never matches a user named `Ale`.
export function hasPersonalMention(text: string, currentUserName: string | undefined): boolean {
  if (!currentUserName) return false;
  const lowerText = text.toLowerCase();
  const lowerName = currentUserName.toLowerCase();
  if (lowerText.includes(`@[${lowerName}]`)) return true;
  const needle = `@${lowerName}`;
  let from = 0;
  for (;;) {
    const idx = lowerText.indexOf(needle, from);
    if (idx === -1) return false;
    const next = lowerText.charAt(idx + needle.length);
    if (next === "" || !/[a-z0-9_]/.test(next)) return true;
    from = idx + needle.length;
  }
}

// A PERSONAL highlight = a direct @mention of the user. Deliberately excludes
// group broadcasts: DND lets these through (Slack/Mattermost behavior — DND
// silences @here but rings on a direct ping), while broadcasts are silenced.
function isPersonalHighlight(
  kind: Extract<NotificationKind, { kind: "channel_message" }>,
  ctx: NotifyContext,
): boolean {
  return hasPersonalMention(kind.text, ctx.currentUserName);
}

// Does this channel message count as a "mention" for the mentions_only gate?
// Broader than isPersonalHighlight: a group broadcast counts here too.
function channelMentionsUser(
  kind: Extract<NotificationKind, { kind: "channel_message" }>,
  ctx: NotifyContext,
): boolean {
  return isPersonalHighlight(kind, ctx) || hasBroadcastMention(kind.text);
}

// Slack's thread rule, as a pure predicate so BOTH the banner decision below and
// the caller's channel-unread (bold / red badge) decision read the same rule.
//
// A thread reply reaches thread PARTICIPANTS only: the people the caller can
// PROVE are in the thread (`threadParticipant`) plus anyone personally
// highlighted by the reply's own text. A channel member who isn't in the thread
// gets nothing — no bold, no badge, no sound, no banner — even on "All new
// messages", because we have no "Also send to #channel" affordance for a replier
// to opt into that.
//
// Group broadcasts do NOT pierce a thread: an @channel/@here inside a reply is
// addressed to the thread, not the channel, so isPersonalHighlight (a personal
// @mention) is the bar here — deliberately
// stricter than channelMentionsUser, which a top-level post still uses.
export function threadReplyReachesUser(
  kind: Extract<NotificationKind, { kind: "channel_message" }>,
  ctx: NotifyContext,
): boolean {
  if (!kind.parentId) return true; // top-level post — nothing changes
  return kind.threadParticipant === true || isPersonalHighlight(kind, ctx);
}

function decideChannel(
  kind: Extract<NotificationKind, { kind: "channel_message" }>,
  ctx: NotifyContext,
  focused: boolean,
): PolicyDecision {
  // AUTHORSHIP, not a delivery target: `isMe`, never `=== ctx.currentUserId`. My own
  // post echoes back stamped with EITHER of my two ids (session-scope.ts
  // ViewerIdentity), and a bare compare let it through — I got bannered for my own
  // message. The `viewing`/`viewingDm*` compares below are a different question (which
  // conversation is on screen) and stay as they are.
  if (kind.senderId && isMe(kind.senderId, ctx.currentUserId))
    return { notify: false, reason: SKIP.own };
  if (kind.channelId === ctx.activeChannelId && focused)
    return { notify: false, reason: SKIP.viewing };
  // Ahead of the pref checks on purpose: a thread reply must not reach a
  // non-participant even on an explicit "all" pref (that pref is about the
  // CHANNEL, and a reply isn't channel traffic).
  if (!threadReplyReachesUser(kind, ctx)) return { notify: false, reason: SKIP.notInThread };
  // Unknown pref → mentions_only, matching the SERVER default
  // (relay-standalone.ts: `prefs.get(id) ?? "mentions_only"`). The web-push path
  // (closed app) already treats an un-set channel as mentions_only, so the
  // in-app banner must agree: a plain non-mention message in a channel with no
  // explicit pref does NOT banner. This is also the safety net for any channel
  // whose pref hasn't loaded client-side (e.g. a brand-new cross-workspace
  // channel) — it never over-notifies. A channel explicitly set to "all" has a
  // stored pref and still notifies on every message.
  const pref = ctx.channelNotifPrefs[kind.channelId] ?? "mentions_only";
  if (pref === "muted") return { notify: false, reason: SKIP.muted };
  // Following a thread is a stronger, EXPLICIT signal than the channel-wide
  // pref, so a reply that reached us by thread participation isn't held behind
  // mentions_only. Server parity: selectThreadReplyPushTargets pushes a follower
  // whose pref is absent or "mentions_only", and drops only "muted" (its "all"
  // exclusion is a push-branch de-dup the client has no equivalent of — one
  // banner path here, so nothing to double-fire).
  const followedReply = !!kind.parentId && kind.threadParticipant === true;
  if (pref === "mentions_only" && !channelMentionsUser(kind, ctx) && !followedReply) {
    return { notify: false, reason: SKIP.noMention };
  }
  // DND suppresses everything except a DIRECT personal mention — group
  // broadcasts (@channel/@here/@all) are silenced under DND.
  if (ctx.dndActive && !isPersonalHighlight(kind, ctx)) {
    return { notify: false, reason: SKIP.dnd };
  }
  return { notify: true, reason: "notify:channel_message" };
}

function decideDmHuman(
  kind: Extract<NotificationKind, { kind: "dm_human" }>,
  ctx: NotifyContext,
  focused: boolean,
): PolicyDecision {
  // AUTHORSHIP (see decideChannel): my own DM echo can carry either of my two ids.
  if (isMe(kind.fromUserId, ctx.currentUserId)) return { notify: false, reason: SKIP.own };
  if (kind.fromUserId === ctx.viewingDmHumanUserId && focused) {
    return { notify: false, reason: SKIP.viewing };
  }
  // DND silences DM banners/sounds too (the message still arrives + badges).
  if (ctx.dndActive) return { notify: false, reason: SKIP.dnd };
  return { notify: true, reason: "notify:dm_human" };
}

function decideDmAgent(
  kind: Extract<NotificationKind, { kind: "dm_agent" }>,
  ctx: NotifyContext,
  focused: boolean,
): PolicyDecision {
  if (kind.remoteAgentId === ctx.viewingDmAgentId && focused) {
    return { notify: false, reason: SKIP.viewing };
  }
  // DND silences agent-DM banners/sounds too.
  if (ctx.dndActive) return { notify: false, reason: SKIP.dnd };
  return { notify: true, reason: "notify:dm_agent" };
}

// An agent is blocked on the viewer's decision: silenced by DND, a muted channel, or while the
// window is focused (the in-app surfaces already carry it).
function decideAgentPermission(
  kind: Extract<NotificationKind, { kind: "agent_permission" }>,
  ctx: NotifyContext,
  focused: boolean,
): PolicyDecision {
  if (focused) return { notify: false, reason: SKIP.viewing };
  if (ctx.dndActive) return { notify: false, reason: SKIP.dnd };
  if (kind.channelId && ctx.channelNotifPrefs[kind.channelId] === "muted") {
    return { notify: false, reason: SKIP.muted };
  }
  return { notify: true, reason: "notify:agent_permission" };
}

export function shouldNotifyFor(kind: NotificationKind, ctx: NotifyContext): PolicyDecision {
  const focused = (ctx.isTabFocused ?? defaultIsTabFocused)();
  switch (kind.kind) {
    case "channel_message":
      return decideChannel(kind, ctx, focused);
    case "dm_human":
      return decideDmHuman(kind, ctx, focused);
    case "dm_agent":
      return decideDmAgent(kind, ctx, focused);
    case "agent_permission":
      return decideAgentPermission(kind, ctx, focused);
    default:
      // task_assigned / task_review_requested: always notify, but DND silences
      // them too (consistent with channel/DM suppression above).
      if (ctx.dndActive) return { notify: false, reason: SKIP.dnd };
      return { notify: true, reason: `notify:${kind.kind}` };
  }
}
